import React, { useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { TreeItem, TreeView } from '@material-ui/lab';

import SaveBackdrop from '../../../components/saveBackdrop';
import GlobalStyles from '../../../global-styles';
import { getContractOrderAccountTree } from '../../../utils/helper';
import { ChevronRightIcon, ExpandMoreIcon, RefreshIcon } from '../../../utils/icons';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, Hidden,
    IconButton, InputLabel, Select, TextField, Tooltip, Typography
} from '../../../utils/material-ui-core';

interface TreeNode {
    id: number;
    name: string;
    parentId: number;
    children: any;
    accountNumber: string,
    count: number
}

interface AccountLookItem {
    code: string;
    name: string;
    programCode: string
}

interface Props {
    accounts: AccountLookItem[],
    open: boolean;
    callActionButton: (confirmOutput: ConfirmOutput) => void
}

const AccountTree: React.FC<Props> = ({ accounts, open, callActionButton }) => {
    const classes = LocalPageStyles();
    const globalStyles = GlobalStyles();

    const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
    const [nodeTypeId, setNodeTypeId] = useState<number>(1);
    const [accountCode, setAccountCode] = useState<string>('');
    const [searchText, setSearchText] = useState<string>('');
    const [expanded, setExpanded] = useState<string[]>([]);
    const [treeNodes, setTreeNodes] = useState<TreeNode[]>([]);
    const [filterTreeNodes, setfilterTreeNodes] = useState<TreeNode[]>([]);

    const getData = async (code, refreshCache) => {

        setfilterTreeNodes([]);

        setSaveInProgress(true);

        var filterAccounts = accounts.filter(account => account.code.toLowerCase() === code.toLowerCase());

        if (filterAccounts.length === 1) {
            const apiResponse = await getContractOrderAccountTree(filterAccounts[0].programCode, filterAccounts[0].code, refreshCache);

            if (apiResponse.success) {
                setTreeNodes(apiResponse.data.nodes);
                setSearchText("");
                getTree("", apiResponse.data.nodes);
            }
        }

        setAccountCode(code);
        setSaveInProgress(false);
    }

    const handleChangeSearchTerm = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
        getTree(event.target.value, treeNodes);
    };

    const getTree = (filterSearchText, data: TreeNode[]) => {
        const map: { [key: number]: TreeNode } = {};
        const tree: TreeNode[] = [];

        data.forEach((node) => {
            map[node.id] = { ...node, children: [] };
        });

        data.forEach((node) => {
            if (node.parentId !== 0) {
                try {
                    map[node.parentId].children.push(map[node.id]);
                }
                catch { }
            } else {
                tree.push(map[node.id]);
            }
        });

        if (filterSearchText === null) {
            filterSearchText = "";
        }
        setfilterTreeNodes(filteredTree(tree, filterSearchText));
    };

    const filteredTree = (nodes: TreeNode[], term: string): TreeNode[] => {
        return nodes.map((node) => {
            const children = node.children ? filteredTree(node.children, term) : [];
            return { ...node, children };
        }).filter((node) => searchTextExists(node.name, term) || node.children.length > 0);
    };

    const searchTextExists = (inputString: string, searchWordsString: string) => {
        let returnValue = false;

        if (searchWordsString.trim() !== "") {
            const searchWords = searchWordsString.toLowerCase().split(";");
            searchWords.forEach(word => {
                if (word !== "" && inputString.includes(word)) {
                    returnValue = true;
                    return;
                }
            });
        }
        else {
            returnValue = true;
        }
        return returnValue;
    }

    const handleToggle = (event: React.ChangeEvent<{}>, nodeIds: string[]) => {
        setExpanded(nodeIds);
    };

    const canRenderTreeNode = (node: TreeNode) => {

        var returnValue = true;

        if (Number(nodeTypeId) === 0) {
            returnValue = true;
        }
        else if (node.children.length > 0) {
            returnValue = true;
        }

        return returnValue;
    }
    const renderTree = (nodes: TreeNode[]) => (
        nodes.map((node) => (
            canRenderTreeNode(node) ?
                <TreeItem
                    key={node.id}
                    nodeId={node.id.toString()}
                    label={
                        <Grid container spacing={0} >
                            <Grid item xs={10} sm={11}>
                                <Grid container spacing={0}>
                                    <Grid item xs={12} md={6} zeroMinWidth>
                                        <Typography variant='caption' noWrap>
                                            {node.name} ({node.id})
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6} zeroMinWidth>
                                        <Typography variant='caption' color='textSecondary' noWrap>
                                            {node.accountNumber}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={2} sm={1} style={{ textAlign: 'right' }} >
                                <Typography variant='caption'>
                                    {node.count}
                                </Typography>
                            </Grid>
                        </Grid>
                    }

                >
                    {node.children.length > 0 && renderTree(node.children)}
                </TreeItem>
                : <> </>

        ))
    );


    const cancel = () => {
        callActionButton({ status: "cancel", data: null })
    }

    useEffect(() => {
        if (open) {
            setTreeNodes([])
            getTree(null, treeNodes);
        }
        else {
            setAccountCode("");
            setTreeNodes([]);
            setSearchText("");
            setfilterTreeNodes([]);
            setNodeTypeId(1);
            setSaveInProgress(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    //const filteredData = filteredTree(getTree(), searchText);

    return (
        <Dialog className={globalStyles.dialogContainer} open={open} maxWidth={"sm"} fullWidth={true}>
            <DialogTitle id="input-dialog-title" className={globalStyles.dialogTitleContainer}>
                Program Parent Tree View
            </DialogTitle>
            <DialogContent dividers>
                <SaveBackdrop open={saveInProgress} />

                <Grid container spacing={3} className={globalStyles.formikContainer}>
                    <Grid item xs={10} sm={11}>
                        <FormControl required className={globalStyles.formControl} fullWidth={true}>
                            <InputLabel id="accountCode-label" shrink={true}>
                                Program/Account
                            </InputLabel>
                            <Select
                                native
                                labelId="accountCode-label"
                                id="accountCode"
                                name="accountCode"
                                value={accountCode}
                                label="Account"
                                onChange={(e) => {
                                    getData(e.currentTarget.value as string, false);
                                }} >
                                <option value=""></option>

                                {accounts.map((account, index) => (
                                    <option value={account.code} key={index}>
                                        {account.name}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={1} sm={1}>
                        <Box p={1} display="flex" justifyContent="center" alignContent="center">
                            <IconButton
                                onClick={(e) => {
                                    getData(accountCode, true);
                                }}>
                                <Tooltip title="Refresh" >
                                    <>
                                        <Hidden only={['xs']}>
                                            <RefreshIcon color="secondary" fontSize='small' />
                                        </Hidden>
                                        <Hidden only={['sm', 'md', 'lg', 'xl',]}>
                                            <RefreshIcon color="secondary" />
                                        </Hidden>
                                    </>
                                </Tooltip>
                            </IconButton>
                        </Box>
                    </Grid>

                    {/* <Grid item xs={3} >
                                    <FormControl required className={globalStyles.formControl} fullWidth={true}>
                                        <InputLabel id="accountCode-label" shrink={true}>
                                            Show {nodeTypeId}
                                        </InputLabel>
                                        <Select
                                            native
                                            labelId="nodeTypeId-label"
                                            id="nodeTypeId"
                                            name="nodeTypeId"
                                            value={nodeTypeId}
                                            label="Account" onChange={(e) => {
                                                setNodeTypeId(e.currentTarget.value as number);
                                            }} >
                                            <option value={0}>All</option>
                                            <option value={1}>Only parents</option>

                                        </Select>
                                    </FormControl>
                                </Grid> */}
                    <Grid item xs={12}>
                        <TextField
                            disabled={accountCode === ""}
                            label="Search"
                            fullWidth
                            value={searchText}
                            onChange={handleChangeSearchTerm}
                            margin="normal"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box className={classes.dialogContentContainer}>

                            <TreeView
                                defaultCollapseIcon={<ExpandMoreIcon />}
                                defaultExpandIcon={<ChevronRightIcon />}
                                expanded={expanded}
                                onNodeToggle={handleToggle}
                            >
                                {renderTree(filterTreeNodes)}
                            </TreeView>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button autoFocus className={globalStyles.buttonDefault} onClick={cancel}>
                    <u>Close</u>
                </Button>
            </DialogActions>
        </Dialog>
    );
};



const LocalPageStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            height: 264,
            flexGrow: 1,
            maxWidth: '100%',
        },
        dialogContentContainer: {
            minHeight: "300px",
            height: "calc(100vh - 450px)",
            overflow: "auto",
            // width: "100%",
        },
    })
);



export default AccountTree;
